// Capacitor-Bridge: läuft beim ersten React-Mount und initialisiert auf
// nativen Plattformen (Android via FCM) die Push-Subscription und das
// App-URL-Routing. Auf Web-Browsern ist `window.Capacitor` undefined —
// die Bridge tut dann nichts.
//
// Konsolidiert den Code aus dem alten app/src/main.js (vor Slice 16
// Capacitor-Switch), der als lokaler Bootstrap lief. Seit server.url
// gesetzt ist, lädt die App direkt die Remote-SPA, und dieser Code
// gehört ins React-Frontend, damit er beim Re-Mount nach Navigation
// auch tatsächlich greift.
//
// Plugin-Imports sind dynamic — der Web-Bundle trägt ohne Capacitor
// nur den ~10 Zeilen langen Guard hier, alles andere wird per Code-
// Splitting nur auf Devices nachgeladen.

const SLUG_KEY = 'lastSlug';
const SLUG_RE = /^\/trips\/([a-z0-9-]+)(?:\/|$)/i;

/**
 * Window-Event, das vom Login-/Register-Flow dispatcht wird (siehe useAuth).
 * Triggert auf nativen Plattformen einen Re-Post der FCM-Subscription mit
 * dem jetzt gültigen Session-Cookie — vorher schlägt /api/native-subscribe
 * mit 401 fehl, sodass Android-User keine native Push bekamen.
 */
export const LOGGED_IN_EVENT = 'mallorca-trips:logged-in';

interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  Plugins?: Record<string, unknown>;
}

function getCapacitor(): CapacitorGlobal | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { Capacitor?: CapacitorGlobal };
  return w.Capacitor ?? null;
}

function isNative(): boolean {
  const cap = getCapacitor();
  return !!cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform();
}

function platform(): 'web' | 'ios' | 'android' {
  const cap = getCapacitor();
  const p = cap?.getPlatform?.() ?? 'web';
  if (p === 'ios' || p === 'android') return p;
  return 'web';
}

function slugFromPath(pathname: string): string | null {
  const m = SLUG_RE.exec(pathname);
  return m ? m[1] : null;
}

// ---- Geolocation-Shim für die native WebView ---------------------------
//
// In der Android-WebView verweigert `navigator.geolocation` ("User denied
// Geolocation"), weil kein nativer Permission-Flow den WebChromeClient-Prompt
// behandelt. Wir patchen `navigator.geolocation` zur Laufzeit auf das
// @capacitor/geolocation-Plugin um, sobald es verfügbar ist — dann laufen
// alle SPA-Geo-Aufrufer (MapPage "Mich orten", LiveLocControl, SosButton,
// LiveFeed) über den nativen Standort-Stack mit echtem Android-Permission-
// Dialog. Im Browser (kein Capacitor) ist das ein No-Op.
//
// Es wird bewusst NICHT `@capacitor/geolocation` importiert — das Plugin
// registriert sich auf nativen Builds selbst unter `Capacitor.Plugins.
// Geolocation`. So bleibt der Web-Bundle frei von der nativen Abhängigkeit.

interface CapPosition {
  coords: {
    latitude: number;
    longitude: number;
    accuracy: number;
    altitude?: number | null;
    altitudeAccuracy?: number | null;
    heading?: number | null;
    speed?: number | null;
  };
  timestamp: number;
}

interface CapGeolocationPlugin {
  getCurrentPosition?: (options?: PositionOptions) => Promise<CapPosition>;
  watchPosition?: (
    options: PositionOptions,
    callback: (position: CapPosition | null, err?: unknown) => void,
  ) => Promise<string>;
  clearWatch?: (options: { id: string }) => Promise<void>;
  requestPermissions?: () => Promise<unknown>;
}

function toGeoError(err: unknown): GeolocationPositionError {
  const message = err instanceof Error ? err.message : String(err ?? 'geolocation_error');
  // Web-API-kompatibles Fehlerobjekt (code 2 = POSITION_UNAVAILABLE).
  return { code: 2, message, PERMISSION_DENIED: 1, POSITION_UNAVAILABLE: 2, TIMEOUT: 3 } as GeolocationPositionError;
}

async function patchNativeGeolocation(): Promise<void> {
  const cap = getCapacitor();
  const geo = cap?.Plugins?.Geolocation as CapGeolocationPlugin | undefined;
  if (!geo || typeof geo.getCurrentPosition !== 'function') return;

  // Permissions vorab anfragen — löst den nativen Android-Dialog aus, bevor
  // der erste getCurrentPosition-Call kommt.
  try {
    await geo.requestPermissions?.();
  } catch {
    // Verweigert → die Calls schlagen einzeln fehl und melden via error-Callback.
  }

  const watchMap = new Map<number, string>();
  let nextWatchId = 1;

  const shim: Geolocation = {
    getCurrentPosition(success, error, options) {
      geo.getCurrentPosition?.(options ?? undefined)
        .then((pos) => success(pos as unknown as GeolocationPosition))
        .catch((err) => error?.(toGeoError(err)));
    },
    watchPosition(success, error, options) {
      const webId = nextWatchId++;
      geo.watchPosition?.(options ?? {}, (pos, err) => {
        if (err) { error?.(toGeoError(err)); return; }
        if (pos) success(pos as unknown as GeolocationPosition);
      })
        .then((capId) => { watchMap.set(webId, capId); })
        .catch((err) => error?.(toGeoError(err)));
      return webId;
    },
    clearWatch(webId) {
      const capId = watchMap.get(webId);
      if (capId) {
        geo.clearWatch?.({ id: capId }).catch(() => {});
        watchMap.delete(webId);
      }
    },
  };

  try {
    Object.defineProperty(navigator, 'geolocation', { value: shim, configurable: true });
  } catch {
    try {
      (navigator as unknown as { geolocation: Geolocation }).geolocation = shim;
    } catch {
      // navigator.geolocation ist read-only und nicht überschreibbar — aufgeben,
      // dann bleibt das (kaputte) WebView-Default-Verhalten.
    }
  }
}

/**
 * Schickt das FCM-Token ans Backend, gebunden an den zuletzt aktiven Slug.
 * Backend speichert die Native-Subscription unter `native-subs:<slug>:*`.
 */
async function postNativeSubscribe(slug: string, fcmToken: string): Promise<void> {
  try {
    await fetch(`/trips/${encodeURIComponent(slug)}/api/native-subscribe`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: fcmToken, platform: platform() === 'ios' ? 'ios' : 'android' }),
    });
  } catch (_) {
    // FCM-Subscriptions sind best-effort — bei Fehler wird der nächste
    // Token-Refresh es nochmal versuchen.
  }
}

/**
 * Init-Funktion. Side-effects: registriert Push-Listener, App-URL-Handler,
 * persistiert den aktuellen Slug. Idempotent durch das `initialized`-Flag.
 */
let initialized = false;

/**
 * Letztes vom FCM-SDK gemeldetes Token. Wird im `registration`-Listener
 * gesetzt und bei Login-Event direkt nochmal an /api/native-subscribe
 * gepostet — ohne auf einen weiteren register()-Call zu warten, der je
 * nach Plattform kein neues `registration`-Event mehr feuert, wenn das
 * Token unverändert ist.
 */
let lastFcmToken: string | null = null;

export async function initCapacitorBridge(): Promise<void> {
  if (initialized) return;
  if (!isNative()) return;
  initialized = true;

  // Geolocation-Shim zuerst — unabhängig von Push, damit Geo auch dann
  // funktioniert, wenn der User Push-Permissions ablehnt.
  await patchNativeGeolocation();

  try {
    const [{ Preferences }, { App }, { PushNotifications }] = await Promise.all([
      import('@capacitor/preferences'),
      import('@capacitor/app'),
      import('@capacitor/push-notifications'),
    ]);

    // Letzten Slug aus URL → Preferences persistieren (für Re-Open).
    const currentSlug = slugFromPath(window.location.pathname);
    if (currentSlug) {
      Preferences.set({ key: SLUG_KEY, value: currentSlug }).catch(() => {});
    }

    // Bei Navigation in der WebView: Slug weiter-tracken (popstate + manuelle).
    window.addEventListener('popstate', () => {
      const slug = slugFromPath(window.location.pathname);
      if (slug) Preferences.set({ key: SLUG_KEY, value: slug }).catch(() => {});
    });

    // App-URL-Open (Deep-Link aus anderer App auf uns)
    App.addListener('appUrlOpen', (event) => {
      try {
        const url = new URL(event.url);
        if (url.origin === window.location.origin) {
          window.location.assign(url.pathname + url.search + url.hash);
        }
      } catch {
        // Ungültige URL — ignorieren.
      }
    });

    // App-State-Change (Foreground): aktuellen Slug merken.
    App.addListener('appStateChange', (state) => {
      if (!state?.isActive) return;
      const slug = slugFromPath(window.location.pathname);
      if (slug) Preferences.set({ key: SLUG_KEY, value: slug }).catch(() => {});
    });

    // Push-Notifications: Permission + Token + Listener
    const perm = await PushNotifications.checkPermissions();
    let receive = perm.receive;
    if (receive === 'prompt' || receive === 'prompt-with-rationale') {
      const requested = await PushNotifications.requestPermissions();
      receive = requested.receive;
    }
    if (receive !== 'granted') return;

    PushNotifications.addListener('registration', async (token) => {
      if (!token?.value) return;
      // Token cachen — Login-Event kann ihn direkt re-posten ohne Re-Register.
      lastFcmToken = token.value;
      // Slug aus URL bevorzugt, sonst aus Preferences-Cache.
      let slug = slugFromPath(window.location.pathname);
      if (!slug) {
        try {
          const r = await Preferences.get({ key: SLUG_KEY });
          slug = (r.value && /^[a-z0-9-]{1,64}$/i.test(r.value)) ? r.value : null;
        } catch { /* ignore */ }
      }
      if (slug) postNativeSubscribe(slug, token.value);
    });

    // Login-Event: Session-Cookie ist jetzt gesetzt, also nochmal posten —
    // beim ersten register() VOR Login antwortet das Backend mit 401.
    window.addEventListener(LOGGED_IN_EVENT, () => {
      void (async () => {
        if (lastFcmToken) {
          let slug = slugFromPath(window.location.pathname);
          if (!slug) {
            try {
              const r = await Preferences.get({ key: SLUG_KEY });
              slug = (r.value && /^[a-z0-9-]{1,64}$/i.test(r.value)) ? r.value : null;
            } catch { /* ignore */ }
          }
          if (slug) postNativeSubscribe(slug, lastFcmToken);
          return;
        }
        // Noch kein Token gesehen → register() triggert den registration-Listener
        // erneut, der diesmal mit Auth-Cookie postet.
        try {
          await PushNotifications.register();
        } catch (err) {
          console.warn('[Capacitor] Re-Register nach Login fehlgeschlagen', err);
        }
      })();
    });

    PushNotifications.addListener('registrationError', (err) => {
      console.warn('[Capacitor] Push-Registration-Error', err);
    });

    // Tap auf Notification → navigieren (innerhalb der App, kein full reload
    // wenn möglich; die SPA-Router übernimmt dann).
    PushNotifications.addListener('pushNotificationActionPerformed', (event) => {
      const url = event?.notification?.data?.url;
      if (typeof url !== 'string' || !url) return;
      try {
        const target = url.startsWith('http')
          ? new URL(url)
          : new URL(url.startsWith('/') ? url : `/${url}`, window.location.origin);
        if (target.origin === window.location.origin) {
          // Same-Origin → der Browser-history-Push reicht; React-Router fängt
          // popstate ab. Bei Cross-Origin (sollte nicht passieren) reicht assign.
          window.history.pushState({}, '', target.pathname + target.search + target.hash);
          window.dispatchEvent(new PopStateEvent('popstate'));
        } else {
          window.location.assign(target.href);
        }
      } catch {
        window.location.assign(url);
      }
    });

    await PushNotifications.register();
  } catch (err) {
    console.warn('[Capacitor] Bridge-Init fehlgeschlagen', err);
  }
}
